import React, { useState, KeyboardEvent } from 'react';
import InputFrame from '../../atoms/InputFrame';
import VariableChip from './VariableChip';
import { isValidEmail } from '../../../utils/EmailUtils';

type MultiEmailInputProps = {
    label: string;
    value: string[];
    onChange: (values: string[]) => void;
    errorMessage?: string;
    helperText?: string;
    placeholder?: string;
    className?: string;
    labelClassName?: string;
};

const MultiEmailInput: React.FC<MultiEmailInputProps> = ({
    label,
    value,
    onChange,
    errorMessage,
    helperText,
    placeholder = "",
    className,
    labelClassName
}) => {
    const [inputValue, setInputValue] = useState('');
    const [localError, setLocalError] = useState('');

    const isVariable = (val: string) => val.startsWith('{{') && val.endsWith('}}');

    const addValue = (val: string) => {
        const trimmed = val.trim();
        if (!trimmed) {
            setInputValue('');
            return;
        }
        if (!isVariable(trimmed) && !isValidEmail(trimmed)) {
            setLocalError(`Email inválido: ${trimmed}`);
            return;
        }
        if (!value.includes(trimmed)) {
            onChange([...value, trimmed]);
        }
        setLocalError('');
        setInputValue('');
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
            if (inputValue.trim()) {
                e.preventDefault();
                addValue(inputValue);
            }
        } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
            onChange(value.slice(0, -1));
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;

        if (val.includes(',') || val.includes(';')) {
            const parts = val.split(/[,;]/);
            const toCommit = parts.slice(0, -1);
            const valid: string[] = [];
            let invalid = '';
            toCommit.forEach(part => {
                const trimmed = part.trim();
                if (!trimmed) return;
                if (!isVariable(trimmed) && !isValidEmail(trimmed)) {
                    invalid = trimmed;
                    return;
                }
                if (!value.includes(trimmed) && !valid.includes(trimmed)) {
                    valid.push(trimmed);
                }
            });
            if (valid.length > 0) {
                onChange([...value, ...valid]);
            }
            setLocalError(invalid ? `Email inválido: ${invalid}` : '');
            setInputValue(invalid || parts[parts.length - 1]);
        } else if (val.endsWith('}}')) {
            addValue(val);
        } else {
            if (localError) setLocalError('');
            setInputValue(val);
        }
    };

    const removeValue = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const effectiveError = errorMessage || localError;

    let textColor = "text-gray-900 dark:text-gray-100";
    let focusColor = "focus:border-blue-600 dark:focus:border-blue-400";

    if (effectiveError) {
        textColor = "text-red-700 dark:text-red-400";
        focusColor = "focus:border-red-600 dark:focus:border-red-400";
    }

    return (
        <div>
            <InputFrame
                label={label}
                errorMessage={effectiveError}
                className={className}
                textColor={textColor}
                labelClassName={labelClassName}
            >
                <div className={`flex flex-wrap gap-1 py-2.5 px-0 w-full text-sm ${textColor} bg-transparent border-0 border-b-2 border-gray-300 ${focusColor} peer min-h-[42px]`}>
                    {value.map((item, index) => (
                        <VariableChip
                            key={index}
                            value={item}
                            isVariable={isVariable(item)}
                            onRemove={() => removeValue(index)}
                            format="email"
                        />
                    ))}
                    <input
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onBlur={() => inputValue.trim() && addValue(inputValue)}
                        placeholder={value.length === 0 ? placeholder : ''}
                        className="flex-1 min-w-[120px] outline-none bg-transparent border-0 focus:ring-0"
                    />
                </div>
            </InputFrame>
            {helperText && !effectiveError && (
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                    {helperText}
                </p>
            )}
        </div>
    );
};

export default MultiEmailInput;
